'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import axios from 'axios';

function responseBodyText(data: unknown): string {
    if (data == null) return '';
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
        const o = data as Record<string, unknown>;
        if (typeof o.message === 'string') return o.message;
    }
    return '';
}

/**
 * Utløpt/manglende ParaOffice-sesjon.
 * Auth-middleware: 401 Invalid Token, eller 403 «A token is required…».
 * IMAP (/mail/) og vanlige tilgangs-403 (JSON «Du har ikke tilgang…») ignoreres.
 */
export function shouldRedirectToLoginOnApiError(error: unknown): boolean {
    if (!axios.isAxiosError(error) || error.response == null) return false;

    const status = error.response.status;
    const url = String(error.config?.url ?? '');

    if (url.includes('/mail/')) return false;
    if (url.includes('/auth/login') || url.includes('/auth/passkey')) return false;

    if (status === 401) return true;

    if (status === 403) {
        const body = responseBodyText(error.response.data).toLowerCase();
        // Eksplisitt auth-middleware / sesjon
        if (body.includes('token is required') || body.includes('invalid token')) {
            return true;
        }
        // Ren tekst-403 (auth middleware bruker .send(string), ikke JSON)
        if (typeof error.response.data === 'string' && body.length > 0) {
            return true;
        }
    }

    return false;
}

function clearClientSession() {
    document.cookie = 'po_loggedin=; path=/; max-age=0';
    document.cookie = 'pomod=; path=/; max-age=0';
    document.cookie = 'poadmin=; path=/; max-age=0';
    try {
        localStorage.removeItem('userData');
        localStorage.removeItem('tokenExpires');
    } catch {
        /* ignore */
    }
}

/** Global axios-interceptor: manglende/ugyldig sesjon → /login. */
export default function AxiosAuthRedirect() {
    const router = useRouter();
    const pathname = usePathname();
    const redirectingRef = useRef(false);

    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (!shouldRedirectToLoginOnApiError(error)) {
                    return Promise.reject(error);
                }

                const onLoginPage =
                    (pathname != null && pathname.startsWith('/login')) ||
                    (typeof window !== 'undefined' && window.location.pathname.startsWith('/login'));

                if (onLoginPage || redirectingRef.current) {
                    return Promise.reject(error);
                }

                redirectingRef.current = true;
                clearClientSession();

                const api = process.env.NEXT_PUBLIC_API_URL;
                if (api) {
                    void axios
                        .post(`${api}/auth/logout`, {}, { withCredentials: true })
                        .catch(() => undefined)
                        .finally(() => {
                            router.push('/login');
                        });
                } else {
                    router.push('/login');
                }

                return Promise.reject(error);
            },
        );

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, [router, pathname]);

    return null;
}
